import React, { useCallback } from 'react';
import {
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { ControlField, FieldMetadata } from '../../contexts/MetadataContext';
import { isEqual } from 'lodash';
import { FieldType, isIntegerType } from '../../system/field-type';
import { isPositive } from '../../utils/validation';
import BooleanRadioField from './control/BooleanRadioField';
import PhoneNumber from './control/PhoneNumber';
import { FormField } from './FormField';
import { FormFieldState, Option } from './FormFieldContext';
import { useDisabledAndReadonlyStyling } from './hooks/useDisabledAndReadonlyStyles';
import { generateDataTestId } from '../../utils';
import { FieldPresentation } from '@brightmls/nextgen.lae.flc';

export interface FieldProps {
  field: ControlField;
  fieldMeta: FieldMetadata; // this is optional elsewhere, but the parent, <MemoizedField />, will not render <Field /> if fieldMeta is undefined
  fieldLabel: string;
  fieldPresentation: FieldPresentation;
  fieldFrame: FormFieldState;
  options?: Option[];
  onFieldValueChange: (value: any) => any;
  handleFieldTouch?: (value: any) => any;
  fieldValue: any;
  fieldSourceValue: any;
  isTouched: boolean;
  showLabel?: boolean;
}

const Field = React.memo<FieldProps>(
  ({
    field,
    fieldMeta,
    fieldValue,
    fieldSourceValue,
    fieldPresentation,
    fieldLabel,
    fieldFrame,
    onFieldValueChange,
    handleFieldTouch,
    isTouched,
    options,
    showLabel = true,
  }) => {
    const setFieldValue = (value: any) => {
      onFieldValueChange(value);
    };

    const handleMenuItemClick = useCallback(
      (optionValue: string) => (event: React.MouseEvent) => {
        event.stopPropagation();

        if (optionValue === fieldValue) {
          onFieldValueChange(null);
        } else {
          onFieldValueChange(optionValue);
        }
      },
      [fieldValue, onFieldValueChange],
    );

    const { isError, isDisabled, isReadOnly } = fieldFrame;

    const commonInputProps = {
      error: isError,
      fullWidth: true,
      placeholder: `Enter ${fieldLabel}`,
      disabled: isDisabled,
      InputProps: { readOnly: isReadOnly },
      label: '',
      sx: {
        // Remove focus styles for disabled/readonly fields
        '& .MuiOutlinedInput-root.Mui-disabled.Mui-focused, & .MuiOutlinedInput-root.Mui-readOnly.Mui-focused':
          {
            '& > fieldset': {
              borderColor: 'rgba(0, 0, 0, 0.23) !important',
              borderWidth: '1px !important',
            },
          },
        // Base styles
        backgroundColor: useDisabledAndReadonlyStyling({
          isDisabled,
          isReadOnly,
        }),
      },
    };

    const numericInputProps = {
      type: 'number',
      sx: {
        '& input[type=number]': {
          MozAppearance: 'textfield',
        },
        '& input[type=number]::-webkit-outer-spin-button': {
          WebkitAppearance: 'none',
          margin: 0,
        },
        '& input[type=number]::-webkit-inner-spin-button': {
          WebkitAppearance: 'none',
          margin: 0,
        },
      },
      onPaste: (event: any) => {
        const inputValue = event.clipboardData.getData('Text');
        const isPositiveNumber = isPositive(fieldMeta?.Definition || {});
        if (inputValue && isPositiveNumber) {
          event.preventDefault();
          if (isNaN(inputValue)) setFieldValue('');
          else setFieldValue(Math.abs(+inputValue));
        }
      },
      onKeyDown: (e: any) => {
        const isInt = isIntegerType(fieldMeta?.Type);
        const isPositiveNumber = isPositive(fieldMeta?.Definition || {});
        const avoidedSymbols = [
          'e',
          'E',
          ...(isInt ? ['.'] : []),
          ...(isPositiveNumber ? ['-'] : []),
        ];
        if (avoidedSymbols.includes(e.key)) {
          return e.preventDefault();
        }
      },
    };

    const renderContent = () => {
      switch (fieldMeta.Type) {
        case FieldType.STRING:
          if (fieldMeta.Lookup) {
            return (
              <FormControl fullWidth error={isError} disabled={isDisabled}>
                <Select
                  data-testid={generateDataTestId(
                    field,
                    fieldMeta,
                    'selectField',
                  )}
                  value={fieldValue ?? ''}
                  onChange={() => {}} // Keep empty onChange to avoid warnings
                  onBlur={handleFieldTouch}
                  onClose={handleFieldTouch}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <Typography sx={{ color: '#767678' }}>
                          Select Option
                        </Typography>
                      );
                    }
                    return selected;
                  }}
                  sx={{ backgroundColor: 'white' }}
                  readOnly={isReadOnly}
                  MenuProps={{
                    PaperProps: {
                      onClick: (e: any) => {
                        // Prevent the menu from closing when clicking an item
                        e.stopPropagation();
                      },
                      sx: {
                        maxHeight: '300px',
                      },
                    },
                  }}
                >
                  {options?.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      onClick={handleMenuItemClick(option.value)}
                      selected={option.value === fieldValue}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            );
          }

          const isPhone = field.Key.includes('_PHONE');
          return isPhone ? (
            <PhoneNumber
              onChange={(phoneNumbericVal: string) =>
                onFieldValueChange(phoneNumbericVal)
              }
              onBlur={handleFieldTouch}
              phoneValue={fieldValue}
              textFieldProps={commonInputProps}
              fieldLabel={fieldLabel}
            />
          ) : (
            <TextField
              data-testid={generateDataTestId(field, fieldMeta, 'textField')}
              {...commonInputProps}
              value={fieldValue || ''}
              onChange={(e) => onFieldValueChange(e.target.value)}
              onBlur={handleFieldTouch}
            />
          );

        case FieldType.BOOL:
          return (
            <BooleanRadioField
              value={fieldValue}
              onChange={(value: boolean | null) => onFieldValueChange(value)}
              disabled={isDisabled}
              readOnly={isReadOnly}
              error={isError}
              field={field}
              fieldLabel={fieldLabel}
            />
          );

        case FieldType.BYTE:
        case FieldType.DECIMAL:
        case FieldType.DOUBLE:
        case FieldType.INT16:
        case FieldType.INT32:
        case FieldType.INT64:
        case FieldType.SINGLE:
          return (
            <TextField
              data-testid={generateDataTestId(field, fieldMeta, 'numberField')}
              {...commonInputProps}
              {...numericInputProps}
              value={fieldValue}
              onChange={(e) => onFieldValueChange(e.target.value)}
              onBlur={handleFieldTouch}
            />
          );

        default:
          return (
            <TextField
              data-testid={generateDataTestId(field, fieldMeta, 'textField')}
              {...commonInputProps}
              value={fieldValue}
              onChange={(e) => onFieldValueChange(e.target.value)}
              onBlur={handleFieldTouch}
            />
          );
      }
    };

    return (
      <FormField
        field={field}
        fieldMeta={fieldMeta}
        fieldPresentation={fieldPresentation}
        fieldValue={fieldValue}
        fieldSourceValue={fieldSourceValue}
        onFieldValueChange={onFieldValueChange}
        showLabel={showLabel}
        options={options}
        {...fieldFrame}
      >
        {renderContent()}
      </FormField>
    );
  },
  (prevProps, nextProps) => {
    if (prevProps.fieldValue !== nextProps.fieldValue) {
      return false;
    }
    // Check if the validation object has changed
    if (!isEqual(prevProps.fieldPresentation, nextProps.fieldPresentation)) {
      return false;
    }

    // Check if the validation object has changed
    if (!isEqual(prevProps.isTouched, nextProps.isTouched)) {
      return false;
    }

    if (prevProps.onFieldValueChange !== nextProps.onFieldValueChange) {
      return false;
    }

    // Check if the validation object has changed
    return isEqual(prevProps.options, nextProps.options);
  },
);

export default Field;
